import { useMarketTokensWithPrices } from 'hooks/useMarketTokens'
import { useMintInfo } from 'hooks/useMintInfo'
import SheenLoader from '../loading/SheenLoader'
import { Table, Td, Th, TrBody, TrHead } from '../shared/TableElements'
import { abbreviateAddress } from 'utils/formatting'
import { formatCurrencyValue, formatNumericValue } from 'utils/numbers'
import { FunnelIcon } from '@heroicons/react/24/outline'
import { ExternalLink } from '@components/shared/ExternalLink'
import { LinkButton } from '../shared/Button'
import { useViewport } from 'hooks/useViewport'
import { TopHolder, useTop20Holders } from 'hooks/useTop20Holders'
import { PublicKey } from '@solana/web3.js'
import { useMemo } from 'react'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import {
  TRADER_ADDRESS_FILTER_KEY,
  makeWalletExplorerUrl,
} from 'utils/constants'
import OpenExplorerLinkIcon from '@components/shared/OpenExplorerLinkIcon'
import { useTranslation } from 'next-i18next'
import store from 'stores/store'

// const generateSearchTerm = (item: TokenHolder, searchValue: string) => {
//   const { owner } = item ?? {}
//   const normalizedSearchValue = searchValue.toLowerCase()
//   const values = owner.toLowerCase()

//   const isMatchingWithOwner = owner
//     ? owner.toLowerCase().indexOf(normalizedSearchValue) >= 0
//     : false
//   const matchingOwnerPercent =
//     isMatchingWithOwner && owner
//       ? normalizedSearchValue.length / owner.length
//       : 0

//   return {
//     token: item,
//     matchingIdx: values.indexOf(normalizedSearchValue),
//     matchingOwnerPercent,
//   }
// }

// const startSearch = (items: TokenHolder[], searchValue: string) => {
//   return items
//     .map((item) => generateSearchTerm(item, searchValue))
//     .filter((item) => item.matchingIdx >= 0)
//     .sort((i1, i2) => i1.matchingIdx - i2.matchingIdx)
//     .sort((i1, i2) => i2.matchingOwnerPercent - i1.matchingOwnerPercent)
//     .map((item) => item.token)
// }

const HoldersTable = ({
  setActiveTradesTab,
}: {
  setActiveTradesTab: (tab: 'trades' | 'holders') => void
}) => {
  const { t } = useTranslation('token-page')
  const { isDesktop } = useViewport()
  const baseToken = store((s) => s.market.baseToken)
  const { mintInfoData } = useMintInfo(baseToken.mint, baseToken.token_program)
  const { baseMintPrice, pricesLoading } = useMarketTokensWithPrices()
  const { mintInfo } = mintInfoData ?? {}
  const { data: holders, isInitialLoading: loadingHolders } = useTop20Holders(
    baseToken.mint,
  )
  const [addressToFilter, setAddressToFilter] = useLocalStorage(
    TRADER_ADDRESS_FILTER_KEY,
  )

  const lastPrice = useMemo(() => {
    if (!baseMintPrice || pricesLoading) return 0
    return baseMintPrice
  }, [baseMintPrice, pricesLoading])

  // const [currentPage, setCurrentPage] = useState(1)
  // const [searchString, setSearchString] = useState('')
  // const itemsPerPage = 50

  // const [currentHoldersToShow, searchResults] = useMemo(() => {
  //   if (!holders) return [[], []]
  //   const start = (currentPage - 1) * itemsPerPage
  //   const end = start + itemsPerPage
  //   if (searchString.length) {
  //     const searchResults = startSearch(holders, searchString)
  //     return [searchResults.slice(start, end), searchResults]
  //   } else {
  //     return [holders.slice(start, end), []]
  //   }
  // }, [holders, currentPage, searchString])

  // const handlePageChange = (newPage: number) => {
  //   setCurrentPage(newPage)
  // }

  const [othersAmount, othersPercentage] = useMemo(() => {
    const supply = mintInfo?.supply
    if (!holders || !holders?.length || !supply) return [0, 0]
    const top20Amount = holders.reduce((a: number, c: TopHolder) => {
      return a + c.uiAmount
    }, 0)
    const amount = supply - top20Amount
    const percentage = (amount / supply) * 100
    return [amount, percentage]
  }, [holders, mintInfo])

  const handleShowTxns = (owner: PublicKey) => {
    setAddressToFilter([...addressToFilter, owner.toString()])
    setActiveTradesTab('trades')
  }

  // const handleInputChange = (value: string) => {
  //   setSearchString(value)
  // }

  // const showPagination =
  //   (!searchString.length && holders?.length && holders.length > 50) ||
  //   (searchString.length && searchResults.length > 50)

  return loadingHolders ? (
    <div className="mt-6 space-y-1 px-6">
      {Array.from({ length: 20 }, (_, i) => (
        <SheenLoader className="flex flex-1" key={i}>
          <div className="h-12 w-full rounded-md bg-th-bkg-2" />
        </SheenLoader>
      ))}
    </div>
  ) : (
    <>
      {/* <div className="relative h-12 w-full md:h-14">
        <input
          className="h-12 w-full bg-th-input-bkg pl-12 pr-10 text-left text-sm text-th-fgd-1 focus:outline-none focus-visible:border-th-fgd-4 md:h-14 md:text-base md:hover:border-th-input-border-hover md:hover:focus-visible:border-th-fgd-4"
          placeholder="Search holders..."
          value={searchString}
          onChange={(e) => handleInputChange(e.target.value)}
        />
        <MagnifyingGlassIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-th-fgd-3" />
        <IconButton
          className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-th-fgd-3"
          disabled={!searchString.length}
          onClick={() => setSearchString('')}
        >
          <XMarkIcon className="h-5 w-5" />
        </IconButton>
      </div> */}
      {!holders?.length ? (
        <div className="mt-6 flex items-center justify-center rounded-xl border border-th-bkg-4 p-6">
          <p className="text-center">{t('holders.error')}</p>
        </div>
      ) : isDesktop ? (
        <Table>
          <thead className="sticky top-0">
            <TrHead>
              <Th className="text-left">{t('holders.table.address')}</Th>
              <Th className="text-right">{t('holders.table.percentage')}</Th>
              <Th className="text-right">{t('holders.table.amount')}</Th>
              <Th className="text-right">{t('holders.table.value')}</Th>
              <Th className="text-right">{t('holders.table.txns')}</Th>
              <Th className="text-right">{t('holders.table.step')}</Th>
            </TrHead>
          </thead>
          <tbody>
            {holders.map((holder, i) => {
              const { address, owner, uiAmount } = holder ?? {}
              const supply = mintInfo?.supply
              let percentage
              if (supply) {
                percentage = (uiAmount / supply) * 100
              }
              let value
              if (lastPrice) {
                value = uiAmount * lastPrice
              }
              return (
                <TrBody
                  className={`${i % 2 ? '' : 'bg-th-bkg-2'}`}
                  key={`${owner}${i}`}
                >
                  <Td>
                    <span>{owner ? abbreviateAddress(owner) : '–'}</span>
                  </Td>
                  <Td>
                    <p className="text-right">
                      {percentage ? `${percentage.toFixed(1)}%` : '–'}
                    </p>
                  </Td>
                  <Td>
                    <p className="text-right">{formatNumericValue(uiAmount)}</p>
                  </Td>
                  <Td>
                    <p className="text-right">
                      {value ? formatCurrencyValue(value) : '–'}
                    </p>
                  </Td>
                  <Td>
                    <div className="flex justify-end">
                      <button
                        className="text-th-fgd-3 focus:outline-none md:hover:text-th-fgd-2"
                        onClick={() => handleShowTxns(address)}
                      >
                        <FunnelIcon className="size-5" />
                      </button>
                    </div>
                  </Td>
                  <Td>
                    <div className="flex justify-end">
                      {owner && (
                        <ExternalLink
                          className="text-th-fgd-3"
                          href={makeWalletExplorerUrl(owner)}
                        >
                          <OpenExplorerLinkIcon className="size-5" />
                        </ExternalLink>
                      )}
                    </div>
                  </Td>
                </TrBody>
              )
            })}
            <TrBody className="bg-th-bkg-2">
              <Td>
                <span>{t('holders.table.others')}</span>
              </Td>
              <Td>
                <p className="text-right">
                  {othersPercentage ? `${othersPercentage.toFixed(1)}%` : '–'}
                </p>
              </Td>
              <Td>
                <p className="text-right">{formatNumericValue(othersAmount)}</p>
              </Td>
              <Td>
                <p className="text-right">
                  {lastPrice
                    ? formatCurrencyValue(othersAmount * lastPrice)
                    : '–'}
                </p>
              </Td>
              <Td>
                <div className="size-5" />
              </Td>
              <Td>
                <div className="size-5" />
              </Td>
            </TrBody>
          </tbody>
        </Table>
      ) : (
        <div
          className={`thin-scroll max-h-[520px] overflow-y-auto rounded-b-lg`}
        >
          {holders.map((holder, i) => {
            const { address, owner, uiAmount } = holder
            const supply = mintInfo?.supply
            let percentage
            if (supply) {
              percentage = (uiAmount / supply) * 100
            }
            let value
            if (lastPrice) {
              value = uiAmount * lastPrice
            }
            return (
              <div
                className={`flex items-center justify-between border-b border-th-bkg-3 bg-th-bkg-2 p-4 last:rounded-b-lg last:border-b-0`}
                key={`${owner}${i}`}
              >
                <div>
                  <p className="mb-1 text-th-fgd-2">
                    {owner ? abbreviateAddress(owner) : '–'}
                  </p>
                  <div className="flex space-x-4 text-xs">
                    <LinkButton
                      className="flex items-center"
                      onClick={() => handleShowTxns(address)}
                    >
                      <span className="mr-1 text-th-fgd-1">
                        {t('holders.mobile.transactions')}
                      </span>
                      <FunnelIcon className="h-3 w-4 text-th-fgd-1" />
                    </LinkButton>
                    {owner && (
                      <ExternalLink
                        className="flex items-center"
                        href={makeWalletExplorerUrl(owner)}
                      >
                        <span className="mr-1">
                          {t('holders.mobile.explorer')}
                        </span>
                        <OpenExplorerLinkIcon className="size-3" />
                      </ExternalLink>
                    )}
                  </div>
                </div>
                <div>
                  <p className="mb-1 font-mono text-th-fgd-2">{`${formatNumericValue(
                    uiAmount,
                  )} ${percentage ? `(${percentage.toFixed(1)}%)` : ''}`}</p>
                  {value ? (
                    <p className="text-right font-mono text-xs">
                      {formatCurrencyValue(value)}
                    </p>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      )}
      {/* {showPagination ? (
        <Pagination
          currentPage={currentPage}
          totalItems={
            searchString.length ? searchResults.length : holders?.length
          }
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      ) : null} */}
    </>
  )
}

export default HoldersTable

// type PaginationProps = {
//   currentPage: number
//   totalItems: number | undefined
//   itemsPerPage: number
//   onPageChange: (page: number) => void
// }

// const Pagination = ({
//   currentPage,
//   totalItems,
//   itemsPerPage,
//   onPageChange,
// }: PaginationProps) => {
//   if (!totalItems) return null
//   const totalPages = Math.ceil(totalItems / itemsPerPage)

//   const handlePrevPage = () => {
//     if (currentPage > 1) onPageChange(currentPage - 1)
//   }

//   const handleNextPage = () => {
//     if (currentPage < totalPages) onPageChange(currentPage + 1)
//   }

//   return (
//     <div className="flex items-center justify-center space-x-3 py-4">
//       <IconButton
//         onClick={handlePrevPage}
//         disabled={currentPage === 1}
//         size="small"
//       >
//         <ChevronLeftIcon className="h-5 w-5" />
//       </IconButton>
//       <span className="text-th-fgd-3">
//         {currentPage} of {totalPages}
//       </span>
//       <IconButton
//         onClick={handleNextPage}
//         disabled={currentPage === totalPages}
//         size="small"
//       >
//         <ChevronRightIcon className="h-5 w-5" />
//       </IconButton>
//     </div>
//   )
// }
